import React, { useEffect, useMemo, useState } from 'react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { useToast } from '../../hooks/use-toast';
import useBranchLabels from '../../hooks/useBranchLabels';
import { filterStudentsBySearch } from './useReportStudents';
import { useStudentSelection } from './useReportStudents';
import { useBulkDownload } from './useBulkDownload';
import { REPORT_DOCUMENT_TYPES } from '../../utils/reportDocumentTypes';
import { handleBlobDownloadResponse } from '../../utils/blobDownload';
import { isNativePlatform } from '../../utils/device';
import DocumentPreviewModal from './DocumentPreviewModal';
import ReportCardStudentTable from './ReportCardStudentTable';
import { FileDown, Settings2 } from 'lucide-react';
import ReportCardTemplateModal from './settings/ReportCardTemplateModal';

export default function ReportCardsDashboard() {
  const { user } = useAuth();
  const { formatLabel } = useBranchLabels();
  const { toast } = useToast();
  const schoolId = user?.school_id;

  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [exams, setExams] = useState([]);

  const [filters, setFilters] = useState({
    academicYearId: '',
    classId: '',
    sectionId: '',
    examId: '',
    status: 'active',
    mode: 'exam',
    includeAttendance: true,
    includeRemarks: true,
    includeSignatures: true,
    includeGrades: true,
  });

  const [loading, setLoading] = useState(false);
  const [students, setStudents] = useState([]);
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => filterStudentsBySearch(students, search), [students, search]);
  const { selectedIds, toggleOne, toggleAll, allSelected, clearSelection } = useStudentSelection(filtered);
  const { runBulk, progress, running, error: bulkError } = useBulkDownload(REPORT_DOCUMENT_TYPES.REPORT_CARD);

  const [activeStudent, setActiveStudent] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [activeTemplateId, setActiveTemplateId] = useState('');

  const buildPreviewParams = (studentId) => ({
    student_id: studentId,
    school_id: schoolId,
    academic_year_id: filters.academicYearId || undefined,
    mode: filters.mode,
    exam_id: filters.mode === 'annual' ? undefined : (filters.examId || undefined),
    template_id: activeTemplateId || undefined,
    include_attendance: filters.includeAttendance ? 1 : 0,
    include_remarks: filters.includeRemarks ? 1 : 0,
    include_signatures: filters.includeSignatures ? 1 : 0,
    include_grades: filters.includeGrades ? 1 : 0,
  });

  useEffect(() => {
    if (!schoolId) return;
    api.get('/classes', { params: { school_id: schoolId } })
      .then((res) => setClasses(res.data?.data || []))
      .catch(() => setClasses([]));
    api.get('/settings/academic-years', { params: { school_id: schoolId } })
      .then((res) => {
        const list = res.data?.data || [];
        setSessions(list);
        if (!filters.academicYearId && list.length) {
          const current = list.find((y) => y.is_current) || list[0];
          if (current) setFilters((p) => ({ ...p, academicYearId: String(current.id) }));
        }
      })
      .catch(() => setSessions([]));
  }, [schoolId]);

  useEffect(() => {
    if (!schoolId) return;
    api.get('/reports/templates', { params: { category: 'report_card', school_id: schoolId } })
      .then((res) => setActiveTemplateId(res.data?.data?.active_template_id || ''))
      .catch(() => setActiveTemplateId(''));
  }, [schoolId]);

  useEffect(() => {
    if (!filters.classId) {
      setSections([]);
      return;
    }
    api.get(`/classes/${filters.classId}`)
      .then((res) => setSections(res.data?.data?.sections || []))
      .catch(() => setSections([]));
  }, [filters.classId]);

  useEffect(() => {
    if (!schoolId) return;
    const params = { school_id: schoolId };
    if (filters.academicYearId) params.academic_year_id = Number(filters.academicYearId);
    api.get('/exams', { params })
      .then((res) => setExams(res.data?.data || []))
      .catch(() => setExams([]));
  }, [schoolId, filters.academicYearId]);

  const canLoad = Boolean(
    schoolId
      && filters.academicYearId
      && filters.classId
      && (filters.mode === 'annual' || filters.examId)
  );

  const fetchSummary = async () => {
    if (!canLoad) return;
    setLoading(true);
    try {
      const params = {
        school_id: schoolId,
        academic_year_id: Number(filters.academicYearId),
        class_id: Number(filters.classId),
        ...(filters.sectionId ? { section_id: Number(filters.sectionId) } : {}),
        status: filters.status,
        mode: filters.mode,
        ...(filters.mode === 'annual' ? {} : { exam_id: Number(filters.examId) }),
      };
      const res = await api.get('/reports/report-card/summary', { params });
      setStudents(res.data?.data?.students || []);
    } catch (e) {
      setStudents([]);
      toast({ title: 'Error', description: e?.response?.data?.message || 'Failed to load students', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSummary(); }, [canLoad, filters.classId, filters.sectionId, filters.status, filters.examId, filters.mode, filters.academicYearId]);

  const openPreview = (student) => {
    setActiveStudent(student);
    setPreviewOpen(true);
  };

  const downloadPdf = async (student) => {
    try {
      const res = await api.get(`/reports/${REPORT_DOCUMENT_TYPES.REPORT_CARD}/pdf`, {
        params: buildPreviewParams(student.id),
        responseType: 'blob',
      });
      await handleBlobDownloadResponse(
        res,
        'pdf',
        `report-card-${student.admission_no || student.id}.pdf`,
      );
    } catch (e) {
      toast({ title: 'Download failed', description: e?.response?.data?.message || e?.message, variant: 'destructive' });
    }
  };

  const printDoc = async (student) => {
    if (isNativePlatform()) {
      toast({
        title: 'Print not available',
        description: 'Use Download to save the report card on mobile.',
        variant: 'destructive',
      });
      return;
    }
    try {
      const res = await api.get(`/reports/${REPORT_DOCUMENT_TYPES.REPORT_CARD}/preview`, { params: buildPreviewParams(student.id) });
      const html = res.data?.data?.html || '';
      const w = window.open('', '_blank');
      if (!w) return;
      w.document.write(html);
      w.document.close();
      w.focus();
      w.print();
    } catch (e) {
      toast({ title: 'Print failed', description: e?.response?.data?.message, variant: 'destructive' });
    }
  };

  const publish = async (studentIds) => {
    try {
      await api.post('/reports/report-card/publish', {
        school_id: schoolId,
        academic_year_id: Number(filters.academicYearId),
        class_id: Number(filters.classId),
        section_id: filters.sectionId ? Number(filters.sectionId) : null,
        status: filters.status,
        mode: filters.mode,
        exam_id: filters.mode === 'annual' ? null : Number(filters.examId),
        student_ids: studentIds,
        template_id: activeTemplateId || null,
        include_attendance: filters.includeAttendance ? 1 : 0,
        include_remarks: filters.includeRemarks ? 1 : 0,
        include_signatures: filters.includeSignatures ? 1 : 0,
        include_grades: filters.includeGrades ? 1 : 0,
      });
      toast({ title: 'Published', description: 'Sent via WhatsApp/Email where available.' });
    } catch (e) {
      toast({ title: 'Publish failed', description: e?.response?.data?.message || 'Failed', variant: 'destructive' });
    }
  };

  const handleBulkDownload = async (output) => {
    try {
      await runBulk({
        studentIds: selectedIds,
        options: {
          academic_year_id: Number(filters.academicYearId),
          exam_id: filters.mode === 'annual' ? null : Number(filters.examId),
          mode: filters.mode,
          template_id: activeTemplateId || null,
          include_attendance: filters.includeAttendance ? 1 : 0,
          include_remarks: filters.includeRemarks ? 1 : 0,
          include_signatures: filters.includeSignatures ? 1 : 0,
          include_grades: filters.includeGrades ? 1 : 0,
        },
        output,
        classId: Number(filters.classId),
      });
      toast({ title: 'Bulk download complete' });
      clearSelection();
    } catch (e) {
      toast({ title: 'Bulk failed', description: e?.message, variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Report Cards</h1>
          <p className="text-muted-foreground mt-1">
            Generate detailed academic report cards with grades, attendance, remarks and printable layouts.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setTemplatesOpen(true)}>
            <Settings2 className="h-4 w-4 mr-2" /> Report Card Settings
          </Button>
          <Button variant="outline" size="sm" onClick={() => setTemplatesOpen(true)}>
            <FileDown className="h-4 w-4 mr-2" /> Templates
          </Button>
        </div>
      </div>

      <Card className="border-border">
        <CardHeader>
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
            <div className="space-y-2">
              <Label>Session</Label>
              <Select value={filters.academicYearId ? String(filters.academicYearId) : ''} onValueChange={(v) => setFilters((p) => ({ ...p, academicYearId: v }))}>
                <SelectTrigger className="bg-muted border-border"><SelectValue placeholder="Academic year" /></SelectTrigger>
                <SelectContent className="bg-muted border-border">
                  {sessions.map((y) => (
                    <SelectItem key={y.id} value={String(y.id)}>{y.name}{y.is_current ? ' (Current)' : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Class</Label>
              <Select value={filters.classId ? String(filters.classId) : ''} onValueChange={(v) => setFilters((p) => ({ ...p, classId: v, sectionId: '' }))}>
                <SelectTrigger className="bg-muted border-border"><SelectValue placeholder="Select class" /></SelectTrigger>
                <SelectContent className="bg-muted border-border">
                  {classes.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>{formatLabel(c.name, c)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Section</Label>
              <Select value={filters.sectionId ? String(filters.sectionId) : 'all'} onValueChange={(v) => setFilters((p) => ({ ...p, sectionId: v === 'all' ? '' : v }))} disabled={!filters.classId}>
                <SelectTrigger className="bg-muted border-border"><SelectValue placeholder="All sections" /></SelectTrigger>
                <SelectContent className="bg-muted border-border">
                  <SelectItem value="all">All</SelectItem>
                  {sections.map((s) => (
                    <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Exam</Label>
              <Select
                value={filters.examId ? String(filters.examId) : ''}
                onValueChange={(v) => setFilters((p) => ({ ...p, examId: v }))}
                disabled={filters.mode === 'annual'}
              >
                <SelectTrigger className="bg-muted border-border"><SelectValue placeholder="Select exam" /></SelectTrigger>
                <SelectContent className="bg-muted border-border">
                  {exams.map((e) => (
                    <SelectItem key={e.id} value={String(e.id)}>{formatLabel(e.name, e)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Student Status</Label>
              <Select value={filters.status} onValueChange={(v) => setFilters((p) => ({ ...p, status: v }))}>
                <SelectTrigger className="bg-muted border-border"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-muted border-border">
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="graduated">Graduated</SelectItem>
                  <SelectItem value="transferred">Transferred</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-5">
            <div className="flex items-center gap-3">
              <Switch
                id="annual-aggregate"
                checked={filters.mode === 'annual'}
                onCheckedChange={(checked) => setFilters((p) => ({ ...p, mode: checked ? 'annual' : 'exam' }))}
              />
              <Label htmlFor="annual-aggregate">Annual Aggregate</Label>
            </div>
            <div className="flex items-center gap-3">
              <Switch id="inc-att" checked={filters.includeAttendance} onCheckedChange={(c) => setFilters((p) => ({ ...p, includeAttendance: Boolean(c) }))} />
              <Label htmlFor="inc-att">Include Attendance</Label>
            </div>
            <div className="flex items-center gap-3">
              <Switch id="inc-rem" checked={filters.includeRemarks} onCheckedChange={(c) => setFilters((p) => ({ ...p, includeRemarks: Boolean(c) }))} />
              <Label htmlFor="inc-rem">Include Remarks</Label>
            </div>
            <div className="flex items-center gap-3">
              <Switch id="inc-sig" checked={filters.includeSignatures} onCheckedChange={(c) => setFilters((p) => ({ ...p, includeSignatures: Boolean(c) }))} />
              <Label htmlFor="inc-sig">Include Signatures</Label>
            </div>
            <div className="flex items-center gap-3">
              <Switch id="inc-grd" checked={filters.includeGrades} onCheckedChange={(c) => setFilters((p) => ({ ...p, includeGrades: Boolean(c) }))} />
              <Label htmlFor="inc-grd">Include Grades</Label>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-4">
        <div className="flex justify-end">
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={!activeStudent}
            onClick={() => setPreviewOpen(true)}
          >
            Preview Full Page
          </Button>
        </div>
        <ReportCardStudentTable
          students={filtered}
          loading={loading}
          search={search}
          onSearchChange={setSearch}
          selectedIds={selectedIds}
          allSelected={allSelected}
          onToggleAll={toggleAll}
          onToggleOne={toggleOne}
          onPreview={openPreview}
          onPdf={(s) => downloadPdf(s)}
          onPrint={(s) => printDoc(s)}
          onPublish={(s) => publish([s.id])}
        />
      </div>

      <Card className="border-border">
        <CardHeader>
          <CardTitle className="text-base">Bulk Report Cards</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="text-sm text-muted-foreground">{selectedIds.length} student(s) selected</div>
          <div className="flex flex-wrap gap-2">
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              disabled={selectedIds.length === 0 || running}
              onClick={() => handleBulkDownload('merged')}
            >
              Bulk Generate PDFs
            </Button>
            <Button
              variant="outline"
              disabled={selectedIds.length === 0 || running}
              onClick={() => handleBulkDownload('zip')}
            >
              Bulk Download ZIP
            </Button>
            <Button
              variant="outline"
              disabled={selectedIds.length === 0}
              onClick={() => publish(selectedIds)}
            >
              Bulk Publish
            </Button>
          </div>
          {(running || bulkError) && (
            <div className="text-sm text-muted-foreground">
              {bulkError ? bulkError : `Generating… ${progress?.done || 0} / ${progress?.total || 0}`}
            </div>
          )}
          <div className="text-xs text-muted-foreground">
            Note: Report cards generated based on selected template and settings.
          </div>
        </CardContent>
      </Card>

      <DocumentPreviewModal
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        documentType={REPORT_DOCUMENT_TYPES.REPORT_CARD}
        params={activeStudent ? buildPreviewParams(activeStudent.id) : null}
        title="Report Card Preview"
      />

      <ReportCardTemplateModal
        open={templatesOpen}
        onOpenChange={setTemplatesOpen}
        schoolId={schoolId}
        activeTemplateId={activeTemplateId}
        onSaved={(id) => { setActiveTemplateId(String(id || '')); }}
      />
    </div>
  );
}

